import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { SingleUploader } from '../../components/MediaUploader';
import api from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const CATEGORIES = ['Buying Tips', 'Selling Tips', 'Market Trends', 'Investment', 'Lifestyle', 'News'];
const EMPTY = { slug: '', title: '', excerpt: '', cover: '', author: '', date: '', category: 'Buying Tips', content: '', published: true };

export default function AdminBlogForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(id);

  useEffect(() => {
    if (isEdit) {
      api.get('/admin/blog').then(r => {
        const p = (r.data || []).find(x => x.id === id);
        if (p) setForm({ ...EMPTY, ...p });
      });
    } else {
      setForm(f => ({ ...f, date: new Date().toISOString().slice(0, 10) }));
    }
  }, [id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) await api.put('/admin/blog/' + id, form);
      else await api.post('/admin/blog', form);
      toast({ title: 'Saved' });
      navigate('/admin/blog');
    } catch { toast({ title: 'Save failed', variant: 'destructive' }); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-4xl">
      <Link to="/admin/blog" className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-[#DC2626] mb-4"><ArrowLeft className="w-4 h-4" /> Back</Link>
      <h1 className="text-2xl font-bold text-neutral-900 mb-6">{isEdit ? 'Edit Post' : 'New Post'}</h1>
      <form onSubmit={submit} className="space-y-5">
        <div className="bg-white p-6 rounded-xl border border-neutral-200 space-y-4">
          <SingleUploader value={form.cover} onChange={(v) => set('cover', v)} label="Cover Image" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2"><label className="text-sm font-medium text-neutral-700 mb-1 block">Title *</label><Input required value={form.title} onChange={(e) => set('title', e.target.value)} /></div>
            <div><label className="text-sm font-medium text-neutral-700 mb-1 block">Slug (URL)</label><Input value={form.slug} onChange={(e) => set('slug', e.target.value)} placeholder="auto-generated if empty" /></div>
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-1 block">Category</label>
              <Select value={form.category} onValueChange={(v) => set('category', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-sm font-medium text-neutral-700 mb-1 block">Author</label><Input value={form.author} onChange={(e) => set('author', e.target.value)} /></div>
            <div><label className="text-sm font-medium text-neutral-700 mb-1 block">Date</label><Input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} /></div>
            <div className="md:col-span-2"><label className="text-sm font-medium text-neutral-700 mb-1 block">Excerpt</label><Textarea rows={3} value={form.excerpt} onChange={(e) => set('excerpt', e.target.value)} /></div>
            <div className="md:col-span-2"><label className="text-sm font-medium text-neutral-700 mb-1 block">Content</label><Textarea rows={12} value={form.content} onChange={(e) => set('content', e.target.value)} placeholder="Write your full article here. Line breaks are preserved." /></div>
            <div className="flex items-center gap-3"><Switch checked={form.published} onCheckedChange={(v) => set('published', v)} /><label className="text-sm font-medium text-neutral-700">Published</label></div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3">
          <Link to="/admin/blog"><Button type="button" variant="outline">Cancel</Button></Link>
          <Button type="submit" disabled={saving} className="btn-primary gap-2">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save</Button>
        </div>
      </form>
    </div>
  );
}
